import { useEffect, useRef, useState } from 'react'
import Typography from '../common/Typography'
import { TabType } from './Sidebar'

export function SidebarTabs({
  view,
  setView,
  onSearchClick
}: {
  view: TabType
  setView: (view: TabType) => void
  onSearchClick?: () => void
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const chatsButtonRef = useRef<HTMLButtonElement>(null)
  const tasksButtonRef = useRef<HTMLButtonElement>(null)

  const [sliderStyle, setSliderStyle] = useState<{
    width: number
    height: number
    transform: string
  }>({ width: 0, height: 0, transform: 'translateX(0px)' })

  // Track if we've initialized to prevent animation on first render
  const [isInitialized, setIsInitialized] = useState(false)

  // Update slider position when view changes
  // Uses offsetWidth/offsetLeft instead of getBoundingClientRect to avoid
  // incorrect sizing when ancestor has CSS transforms (e.g., scale animations)
  useEffect(() => {
    const updateSlider = () => {
      const container = containerRef.current
      const activeButton = view === 'chats' ? chatsButtonRef.current : tasksButtonRef.current

      if (!container || !activeButton) return

      const offsetX = activeButton.offsetLeft

      setSliderStyle({
        width: activeButton.offsetWidth,
        height: activeButton.offsetHeight,
        transform: `translateX(${offsetX}px)`
      })

      // Enable transitions after first position is set
      if (!isInitialized) {
        requestAnimationFrame(() => setIsInitialized(true))
      }
    }

    updateSlider()

    // Re-measure after fonts finish loading (fixes misshapen pill on hard refresh
    // when custom font hasn't loaded yet during the initial measurement)
    void document.fonts.ready.then(() => updateSlider())

    // Update on resize
    window.addEventListener('resize', updateSlider)
    return () => window.removeEventListener('resize', updateSlider)
  }, [view, isInitialized])

  return (
    <>
      <div className='px-6 min-h-9 flex items-center justify-between'>
        <div ref={containerRef} className='relative flex items-center gap-2'>
          {/* Sliding background indicator */}
          <div
            className='absolute rounded-[24px] bg-[#65558f]'
            style={{
              width: sliderStyle.width,
              height: sliderStyle.height,
              transform: sliderStyle.transform,
              transition: isInitialized ? 'transform 200ms ease-out, width 200ms ease-out' : 'none',
              top: '50%',
              marginTop: -(sliderStyle.height / 2),
              left: 0,
              // Hide until properly positioned to prevent animation on load
              opacity: sliderStyle.width > 0 ? 1 : 0
            }}
          />
          <button
            ref={chatsButtonRef}
            onClick={() => setView('chats')}
            className='relative z-10 px-3 py-1.5 rounded-[24px] transition-colors select-none'
          >
            <Typography
              fontFamily='geist'
              fontSize='bodyMd'
              fontWeight='medium'
              className={
                view === 'chats'
                  ? 'text-tokens-neutral-text-white'
                  : 'text-tokens-neutral-text-secondary'
              }
            >
              Chats
            </Typography>
          </button>
          <button
            ref={tasksButtonRef}
            onClick={() => setView('tasks')}
            className='relative z-10 px-3 py-1.5 rounded-[24px] transition-colors select-none'
          >
            <Typography
              fontFamily='geist'
              fontSize='bodyMd'
              fontWeight='medium'
              className={
                view === 'tasks'
                  ? 'text-tokens-neutral-text-white'
                  : 'text-tokens-neutral-text-secondary'
              }
            >
              Tasks
            </Typography>
          </button>
        </div>
        <button
          aria-label='Open search'
          className='p-1.5 rounded-md hover:bg-gray-50'
          onClick={onSearchClick}
        >
          <svg
            width='20'
            height='20'
            viewBox='0 0 20 20'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M8.33158 3.33317C5.57016 3.33317 3.33158 5.57175 3.33158 8.33317C3.33158 11.0946 5.57016 13.3332 8.33158 13.3332C11.093 13.3332 13.3316 11.0946 13.3316 8.33317C13.3316 5.57175 11.093 3.33317 8.33158 3.33317ZM1.66492 8.33317C1.66492 4.65127 4.64969 1.6665 8.33158 1.6665C12.0135 1.6665 14.9983 4.65127 14.9983 8.33317C14.9983 9.87376 14.4757 11.2923 13.5981 12.4212L18.0875 16.9106L16.909 18.0891L12.4196 13.5997C11.2907 14.4773 9.87218 14.9998 8.33158 14.9998C4.64969 14.9998 1.66492 12.0151 1.66492 8.33317Z'
              fill='#8E8E8E'
            />
          </svg>
        </button>
      </div>
    </>
  )
}
